import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCapabilities } from "@/hooks/useCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Grid3X3, X, Save, Search } from "lucide-react";

export default function SubareasTab({ clientId }) {
  const [subareas, setSubareas] = useState([]);
  const [areas, setAreas] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState("");
  const [formAreaId, setFormAreaId] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [search, setSearch] = useState("");
  // Mesma decisão das rotas /subareas na área selecionada. Subáreas têm
  // permissão própria: "Criar Área" não concede "Criar Subárea".
  const caps = useCapabilities(["subareas.create", "subareas.edit", "subareas.delete"], {
    clientId,
    unitId: selectedUnitId || null,
    areaId: selectedAreaId || null,
  });

  const loadUnits = useCallback(async () => {
    try {
      const { data } = await api.get("/units", { params: { client_id: clientId } });
      const list = (Array.isArray(data) ? data : data?.items || []).filter((u) => u.client_id === clientId);
      setUnits(list);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao carregar unidades");
    }
  }, [clientId]);

  const loadAreas = useCallback(async () => {
    if (!selectedUnitId) {
      setAreas([]);
      return;
    }
    try {
      const { data } = await api.get("/areas", { params: { unit_id: selectedUnitId } });
      setAreas(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao carregar areas");
    }
  }, [selectedUnitId]);

  const loadSubareas = useCallback(async () => {
    if (!selectedAreaId) {
      setSubareas([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/subareas", { params: { area_id: selectedAreaId } });
      setSubareas(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao carregar subareas");
    } finally {
      setLoading(false);
    }
  }, [selectedAreaId]);

  useEffect(() => { loadUnits(); }, [loadUnits]);

  useEffect(() => { loadAreas(); }, [loadAreas]);

  useEffect(() => { loadSubareas(); }, [loadSubareas]);
  const handleCreate = () => { setEditing("new"); setFormName(""); setFormAreaId(selectedAreaId || areas[0]?.id || ""); };
  const handleEdit = (s) => { setEditing(s.id); setFormName(s.name || ""); setFormAreaId(s.area_id || ""); };
  const handleCancel = () => { setEditing(null); setFormName(""); setFormAreaId(""); };

  const handleSave = async () => {
    if (!formName.trim() || !formAreaId) { toast.error("Nome e area sao obrigatorios"); return; }
    try {
      if (editing === "new") { await api.post("/subareas", { area_id: formAreaId, name: formName }); toast.success("Subarea criada"); }
      else { await api.put(`/subareas/${editing}`, { area_id: formAreaId, name: formName }); toast.success("Subarea atualizada"); }
      handleCancel(); loadSubareas();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao salvar"); }
  };

  const handleDelete = async (id) => {
    if (confirm("Excluir esta subarea?")) {
      try { await api.delete(`/subareas/${id}`); toast.success("Subarea excluida"); loadSubareas(); }
      catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao excluir"); }
    }
  };

  const filtered = subareas.filter((s) => {
    if (search.trim() && !(s.name || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const getAreaName = (id) => areas.find((a) => a.id === id)?.name || "-";
  const getUnitName = (areaId) => {
    const area = areas.find((a) => a.id === areaId);
    if (!area) return "-";
    const unit = units.find((u) => u.id === area.unit_id);
    return unit?.name || "-";
  };

  if (loading) return <Skeleton className="h-40 w-full" />;

  if (units.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Grid3X3 className="h-12 w-12 mx-auto mb-3 opacity-40" />
        <p className="text-sm">Nenhuma unidade cadastrada para este cliente.</p>
        <p className="text-xs mt-1">Cadastre uma unidade e uma área primeiro para gerenciar subáreas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
        <p className="text-xs text-muted-foreground">Selecione a unidade e a área para visualizar as subáreas:</p>
        <div className="flex flex-wrap gap-2">
          <select value={selectedUnitId} onChange={(e) => { setSelectedUnitId(e.target.value); setSelectedAreaId(""); }} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm flex-1 min-w-[150px]" data-testid="subareas-unit-select">
            <option value="">Selecione a unidade...</option>
            {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={selectedAreaId} onChange={(e) => setSelectedAreaId(e.target.value)} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm flex-1 min-w-[150px]" disabled={!selectedUnitId} data-testid="subareas-area-select">
            <option value="">Selecione a área...</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>

      {!selectedUnitId || !selectedAreaId ? (
        <p className="text-sm text-muted-foreground text-center py-8">Selecione uma unidade e uma área para visualizar as subáreas.</p>
      ) : (
        <>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{filtered.length} subarea(s)</Badge>
        {caps.can("subareas.create") && <Button size="sm" onClick={handleCreate} disabled={!selectedAreaId} data-testid="subareas-new"><Plus className="h-4 w-4 mr-1" />Nova Subarea</Button>}
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar subarea..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-sm w-48" />
        </div>
      </div>
        </>
      )}
      {editing && (
        <Card className="p-3 border-primary">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <Input autoFocus value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Nome da subarea" className="flex-1" data-testid="subareas-form-name" />
            <select value={formAreaId} onChange={(e) => setFormAreaId(e.target.value)} className="h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="subareas-form-area">
              <option value="">Selecione a area</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <Button size="sm" onClick={handleSave} data-testid="subareas-form-save"><Save className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={handleCancel}><X className="h-4 w-4" /></Button>
          </div>
        </Card>
      )}
      {selectedAreaId && filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Nenhuma subarea cadastrada para esta área</p>
      ) : selectedAreaId ? (
        <div className="divide-y divide-border border border-border rounded-md">
          {filtered.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3" data-testid={`subarea-row-${s.id}`}>
              <div className="flex items-center gap-3">
                <Grid3X3 className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">Área: {getAreaName(s.area_id)} | Unidade: {getUnitName(s.area_id)}</p>
                </div>
              </div>
              <div className="flex gap-1">
                {caps.can("subareas.edit") && <Button size="icon" variant="ghost" onClick={() => handleEdit(s)} data-testid={`subarea-edit-${s.id}`}><Pencil className="h-3 w-3" /></Button>}
                {caps.can("subareas.delete") && <Button size="icon" variant="ghost" onClick={() => handleDelete(s.id)} data-testid={`subarea-delete-${s.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
